import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { dispatchDataError, dispatchNotice, describeSupabaseError } from "@/store/shared/dataErrors";
import { useSession } from "@/session/SessionProvider";
import { traiterPaiementAbonnementRpc } from "@/store/platform/paiementAbonnement";
import { envoyerEmailValidationPaiement, type EmailValidationResult } from "@/store/platform/emailValidation";
import {
  deleteAbonnementRecord,
  deletePaiementRecord,
  fetchBillingData,
  insertAbonnementRecord,
  insertPaiementRecord,
  tempId,
  updateAbonnementRecord,
  updateAbonnementStatutRecord,
  updatePaiementRecord,
  updatePaiementStatutRecord,
} from "@/store/platform/billingData";
import type {
  AbonnementStatut,
  PaiementStatut,
  PlatformAbonnement,
  PlatformAbonnementInput,
  PlatformPaiement,
  PlatformPaiementInput,
} from "./types";

interface BillingContextValue {
  abonnements: PlatformAbonnement[];
  paiements: PlatformPaiement[];
  chargement: boolean;
  erreur: string | null;
  recharger: () => Promise<void>;
  addAbonnement: (input: PlatformAbonnementInput) => PlatformAbonnement;
  updateAbonnement: (id: string, input: PlatformAbonnementInput) => void;
  removeAbonnement: (id: string) => void;
  changeAbonnementStatut: (id: string, statut: AbonnementStatut) => void;
  getAbonnement: (id: string) => PlatformAbonnement | undefined;
  abonnementsByCompte: (compteId: string) => PlatformAbonnement[];
  countPaiementsByAbonnement: (abonnementId: string) => number;
  paiementsByAbonnement: (abonnementId: string) => PlatformPaiement[];
  addPaiement: (input: PlatformPaiementInput) => PlatformPaiement;
  updatePaiement: (id: string, input: PlatformPaiementInput) => void;
  removePaiement: (id: string) => void;
  changePaiementStatut: (id: string, statut: PaiementStatut) => void;
  /** Valide ou refuse un paiement (RPC serveur : active l'abonnement en cas de validation). */
  traiterPaiement: (id: string, valider: boolean, motif?: string) => Promise<boolean>;
  /**
   * Renvoie manuellement l'e-mail de confirmation d'un paiement validé
   * (réservé au Super Admin, protégé côté serveur, idempotent).
   */
  renvoyerEmailValidation: (id: string) => Promise<EmailValidationResult>;
  getPaiement: (id: string) => PlatformPaiement | undefined;
}

const BillingContext = createContext<BillingContextValue | null>(null);

/**
 * Abonnements et paiements de la plateforme — branchés sur les tables RÉELLES
 * `abonnements` et `paiements_abonnements` (RLS Super Admin déjà en place).
 *
 * Aucune donnée fictive : les collections sont vides tant que la base l'est.
 */
export function BillingProvider({ children }: { children: ReactNode }) {
  const { session } = useSession();
  const [abonnements, setAbonnements] = useState<PlatformAbonnement[]>([]);
  const [paiements, setPaiements] = useState<PlatformPaiement[]>([]);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const recharger = useCallback(async () => {
    if (!session || session.role !== "super_admin") {
      setAbonnements([]);
      setPaiements([]);
      return;
    }
    setChargement(true);
    setErreur(null);
    try {
      const snapshot = await fetchBillingData();
      setAbonnements(snapshot.abonnements);
      setPaiements(snapshot.paiements);
    } catch (caught) {
      setErreur("Le chargement des abonnements a échoué.");
      dispatchDataError({
        scope: "Abonnements",
        message: "Impossible de charger les abonnements de la plateforme.",
        technical: describeSupabaseError(caught),
      });
      console.error("[Facturation] chargement Supabase échoué", caught);
    } finally {
      setChargement(false);
    }
  }, [session]);

  useEffect(() => {
    void recharger();
  }, [recharger]);

  const addAbonnement = useCallback(
    (input: PlatformAbonnementInput) => {
      const optimistic: PlatformAbonnement = { ...input, id: tempId(), createdAt: new Date().toISOString() };
      setAbonnements((prev) => [optimistic, ...prev]);
      void insertAbonnementRecord(input)
        .then(() => recharger())
        .catch((caught) => {
          setAbonnements((prev) => prev.filter((item) => item.id !== optimistic.id));
          dispatchDataError({
            scope: "Abonnements",
            message: "L'enregistrement de l'abonnement a échoué.",
            technical: describeSupabaseError(caught),
          });
        });
      return optimistic;
    },
    [recharger]
  );

  const updateAbonnement = useCallback(
    (id: string, input: PlatformAbonnementInput) => {
      const previous = abonnements.find((item) => item.id === id);
      setAbonnements((prev) => prev.map((item) => (item.id === id ? { ...item, ...input } : item)));
      void updateAbonnementRecord(id, input)
        .then(() => recharger())
        .catch((caught) => {
          if (previous) setAbonnements((prev) => prev.map((item) => (item.id === id ? previous : item)));
          dispatchDataError({
            scope: "Abonnements",
            message: "La modification de l'abonnement a échoué.",
            technical: describeSupabaseError(caught),
          });
        });
    },
    [abonnements, recharger]
  );

  const removeAbonnement = useCallback(
    (id: string) => {
      const previous = abonnements.find((item) => item.id === id);
      setAbonnements((prev) => prev.filter((item) => item.id !== id));
      void deleteAbonnementRecord(id)
        .then(() => recharger())
        .catch((caught) => {
          if (previous) setAbonnements((prev) => [previous, ...prev]);
          dispatchDataError({
            scope: "Abonnements",
            message: "La suppression de l'abonnement a échoué.",
            technical: describeSupabaseError(caught),
          });
        });
    },
    [abonnements, recharger]
  );

  const changeAbonnementStatut = useCallback(
    (id: string, statut: AbonnementStatut) => {
      const previous = abonnements.find((item) => item.id === id);
      setAbonnements((prev) => prev.map((item) => (item.id === id ? { ...item, statut } : item)));
      void updateAbonnementStatutRecord(id, statut)
        .then(() => recharger())
        .catch((caught) => {
          if (previous) setAbonnements((prev) => prev.map((item) => (item.id === id ? previous : item)));
          dispatchDataError({
            scope: "Abonnements",
            message: "Le changement de statut de l'abonnement a échoué.",
            technical: describeSupabaseError(caught),
          });
        });
    },
    [abonnements, recharger]
  );

  const getAbonnement = useCallback((id: string) => abonnements.find((item) => item.id === id), [abonnements]);

  const abonnementsByCompte = useCallback(
    (compteId: string) => abonnements.filter((item) => item.compteId === compteId),
    [abonnements]
  );

  const paiementsByAbonnement = useCallback(
    (abonnementId: string) =>
      paiements
        .filter((item) => item.abonnementId === abonnementId)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    [paiements]
  );

  const countPaiementsByAbonnement = useCallback(
    (abonnementId: string) => paiements.filter((item) => item.abonnementId === abonnementId).length,
    [paiements]
  );

  const addPaiement = useCallback(
    (input: PlatformPaiementInput) => {
      const optimistic: PlatformPaiement = {
        ...input,
        preuveUrl: input.preuveUrl ?? "",
        commentaire: input.commentaire ?? "",
        id: tempId(),
        createdAt: new Date().toISOString(),
        emailStatut: "non_envoye",
        emailErreur: "",
      };
      setPaiements((prev) => [optimistic, ...prev]);
      void insertPaiementRecord(input)
        .then(() => recharger())
        .catch((caught) => {
          setPaiements((prev) => prev.filter((item) => item.id !== optimistic.id));
          dispatchDataError({
            scope: "Paiements",
            message: "L'enregistrement du paiement a échoué.",
            technical: describeSupabaseError(caught),
          });
        });
      return optimistic;
    },
    [recharger]
  );

  const updatePaiement = useCallback(
    (id: string, input: PlatformPaiementInput) => {
      const previous = paiements.find((item) => item.id === id);
      setPaiements((prev) => prev.map((item) => (item.id === id ? { ...item, ...input } : item)));
      void updatePaiementRecord(id, input)
        .then(() => recharger())
        .catch((caught) => {
          if (previous) setPaiements((prev) => prev.map((item) => (item.id === id ? previous : item)));
          dispatchDataError({
            scope: "Paiements",
            message: "La modification du paiement a échoué.",
            technical: describeSupabaseError(caught),
          });
        });
    },
    [paiements, recharger]
  );

  const removePaiement = useCallback(
    (id: string) => {
      const previous = paiements.find((item) => item.id === id);
      setPaiements((prev) => prev.filter((item) => item.id !== id));
      void deletePaiementRecord(id)
        .then(() => recharger())
        .catch((caught) => {
          if (previous) setPaiements((prev) => [previous, ...prev]);
          dispatchDataError({
            scope: "Paiements",
            message: "La suppression du paiement a échoué.",
            technical: describeSupabaseError(caught),
          });
        });
    },
    [paiements, recharger]
  );

  const changePaiementStatut = useCallback(
    (id: string, statut: PaiementStatut) => {
      const previous = paiements.find((item) => item.id === id);
      setPaiements((prev) => prev.map((item) => (item.id === id ? { ...item, statut } : item)));
      void updatePaiementStatutRecord(id, statut)
        .then(() => recharger())
        .catch((caught) => {
          if (previous) setPaiements((prev) => prev.map((item) => (item.id === id ? previous : item)));
          dispatchDataError({
            scope: "Paiements",
            message: "Le changement de statut du paiement a échoué.",
            technical: describeSupabaseError(caught),
          });
        });
    },
    [paiements, recharger]
  );

  const traiterPaiement = useCallback(
    async (id: string, valider: boolean, motif?: string) => {
      const previous = paiements.find((item) => item.id === id);
      const optimiste: PaiementStatut = valider ? "valide" : "rejete";
      setPaiements((prev) => prev.map((item) => (item.id === id ? { ...item, statut: optimiste } : item)));
      try {
        await traiterPaiementAbonnementRpc(id, valider, motif);
        if (valider) {
          // E-mail de confirmation APRÈS validation réussie uniquement.
          // NON bloquant : un échec d'envoi ne remet jamais en cause la
          // validation du paiement ni l'activation de l'abonnement.
          try {
            const result = await envoyerEmailValidationPaiement(id);
            if (!result.envoye) {
              console.warn("[Paiements] e-mail de validation non envoyé", result.raison, result.detail ?? "");
            }
          } catch (mailError) {
            console.warn("[Paiements] envoi de l'e-mail de validation échoué", mailError);
          }
        }
        await recharger();
        return true;
      } catch (caught) {
        if (previous) setPaiements((prev) => prev.map((item) => (item.id === id ? previous : item)));
        dispatchDataError({
          scope: "Paiements",
          message: valider ? "La validation du paiement a échoué." : "Le refus du paiement a échoué.",
          technical: describeSupabaseError(caught),
        });
        return false;
      }
    },
    [paiements, recharger]
  );

  const renvoyerEmailValidation = useCallback(
    async (id: string) => {
      try {
        const result = await envoyerEmailValidationPaiement(id);
        await recharger();
        if (result.envoye) {
          dispatchNotice({
            scope: "Paiements",
            tone: "success",
            message: "E-mail de confirmation envoyé au client.",
          });
        } else {
          dispatchDataError({
            scope: "Paiements",
            message:
              result.detail
                ? `E-mail non envoyé : ${result.detail}`
                : result.raison || "E-mail non envoyé.",
          });
        }
        return result;
      } catch (caught) {
        dispatchDataError({
          scope: "Paiements",
          message: "L'envoi de l'e-mail de confirmation a échoué.",
          technical: describeSupabaseError(caught),
        });
        return { ok: false, envoye: false } as EmailValidationResult;
      }
    },
    [recharger]
  );

  const getPaiement = useCallback((id: string) => paiements.find((item) => item.id === id), [paiements]);

  const value = useMemo(
    () => ({
      abonnements,
      paiements,
      chargement,
      erreur,
      recharger,
      addAbonnement,
      updateAbonnement,
      removeAbonnement,
      changeAbonnementStatut,
      getAbonnement,
      abonnementsByCompte,
      countPaiementsByAbonnement,
      paiementsByAbonnement,
      addPaiement,
      updatePaiement,
      removePaiement,
      changePaiementStatut,
      traiterPaiement,
      renvoyerEmailValidation,
      getPaiement,
    }),
    [
      abonnements,
      paiements,
      chargement,
      erreur,
      recharger,
      addAbonnement,
      updateAbonnement,
      removeAbonnement,
      changeAbonnementStatut,
      getAbonnement,
      abonnementsByCompte,
      countPaiementsByAbonnement,
      paiementsByAbonnement,
      addPaiement,
      updatePaiement,
      removePaiement,
      changePaiementStatut,
      traiterPaiement,
      renvoyerEmailValidation,
      getPaiement,
    ]
  );

  return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
}

export function useBilling(): BillingContextValue {
  const context = useContext(BillingContext);
  if (!context) {
    throw new Error("useBilling doit être utilisé à l'intérieur de BillingProvider");
  }
  return context;
}